import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import Spinner from "./spinner"
import { cn } from "@aw/lib"

type Color = "space-cadet" | "purple" | "green" | "red" | "gradient"
type ButtonVariant = "primary" | "secondary" | "tertiary" | "tonal" | "default"

function combinedColorVariant({
    color,
    variant
}: {
    color: Color | undefined
    variant: ButtonVariant
}) {
    // `--primary-*` is NOT a constant — org swaps it per module group (purple/green/space-cadet), so every
    // step must read in all 3 (tonal label is `-50`; `-200` fails AA on space-cadet). Label + hover are ONE
    // var-backed class, not a `dark:` pair — twMerge keeps both modifier sets, so a pair defeats callers.
    // Fill is a role token, not --primary-500: in admin that step IS #232b3d = --bg-2, so a filled primary painted its own background.
    const defaultColor = {
        primary: "bg-(--btn-primary-bg) hover:bg-(--btn-primary-hover) text-(--btn-primary-fg)",
        secondary: "bg-transparent text-foreground hover:bg-card border border-gunmetal-300 dark:border-gunmetal-300-dark",
        tertiary: "bg-transparent text-foreground hover:bg-card",
        // Tonal hover anchors to --primary-300, not the base: in admin --primary-base IS #283044 =
        // --bg-3, so a base wash composites to the same pixel as the default — no hover affordance.
        tonal: "bg-(--primary-50) hover:bg-(--primary-200) text-(--primary-800) dark:bg-(--primary-base)/16 dark:hover:bg-(--primary-300)/32 dark:text-(--primary-50) dark:disabled:bg-subtle",
        default: "bg-(--btn-primary-bg) hover:bg-(--btn-primary-hover) text-(--btn-primary-fg)",
    }

    const colorMap = {
        "space-cadet": {
            primary: "bg-space-cadet-500 hover:bg-space-cadet-600 dark:bg-space-cadet-50 dark:hover:bg-gunmetal-lightest! text-lavender-50 dark:text-space-cadet-800",
            secondary: "bg-transparent hover:bg-card text-foreground",
            tertiary: "bg-transparent text-foreground hover:bg-card disabled:text-foreground-disabled",
            tonal: "bg-space-cadet-50 hover:bg-space-cadet-100 text-foreground disabled:bg-space-cadet-50 disabled:text-gunmetal-200 dark:disabled:text-foreground-disabled",
            default: "bg-space-cadet-500 hover:bg-space-cadet-600 dark:bg-space-cadet-50 dark:hover:bg-gunmetal-lightest! text-lavender-50 dark:text-space-cadet-800",
        },
        "purple": {
            // No `dark:hover:` pair: violet darkens on hover in BOTH themes, matching `defaultColor.primary`
            // (--btn-primary-hover is #6249d4 in dark = purple-600). No violet step above -600 clears AA.
            primary: "bg-purple-base hover:bg-purple-600 text-lavender-50 dark:text-foreground-on-accent",
            secondary: "bg-transparent hover:bg-card text-foreground",
            tertiary: "bg-transparent text-foreground hover:bg-card",
            tonal: "bg-purple-50 hover:bg-purple-100 dark:bg-purple-base/16 dark:hover:bg-purple-300/24 text-purple-800 dark:text-purple-200 disabled:bg-purple-100 disabled:text-gunmetal-200 dark:disabled:bg-subtle dark:disabled:text-foreground-disabled",
            default: "bg-purple-base hover:bg-purple-600 text-lavender-50 dark:text-foreground-on-accent",
        },
        "green": {
            primary: "bg-green-base hover:bg-green-500 dark:hover:bg-green-300 text-foreground dark:text-green-800",
            secondary: "bg-transparent hover:bg-card text-foreground",
            tertiary: "bg-transparent text-foreground hover:bg-card",
            tonal: "bg-green-50 hover:bg-green-100 text-foreground disabled:bg-green-100",
            default: "bg-green-base hover:bg-green-500 dark:hover:bg-green-300 text-foreground dark:text-green-800",
        },
        "red": {
            primary: "border border-red-base bg-red-base !text-white hover:bg-red-600 text-red-base disabled:border-none",
            // Non-filled label reads --btn-danger-fg, NOT `text-red-base`: the bare token is rewritten by the
            // unlayered danger remap, which beats any layered `dark:` pair (styles.css 02e). Border keeps red-base.
            secondary: "bg-transparent hover:bg-red-50 dark:hover:bg-red-base/20 text-(--btn-danger-fg) border border-red-base disabled:border-none",
            tertiary: "bg-transparent text-(--btn-danger-fg) hover:bg-red-50 dark:hover:bg-red-base/20",
            tonal: "bg-transparent text-(--btn-danger-fg) hover:bg-red-50 dark:hover:bg-red-base/20",
            default: "border border-red-base bg-red-base text-white hover:bg-red-50 dark:hover:bg-red-base/20 text-red-base disabled:border-none",
        },
        "gradient": {
            primary: "border-0 bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)] text-lavender-50 dark:text-foreground-on-accent hover:brightness-[0.8] dark:hover:brightness-125",
            // Padding-box routes through var(--bg-3) in dark; the hardcoded white pill showed through.
            secondary: "border-2 border-transparent [background:linear-gradient(white,white)_padding-box,linear-gradient(80deg,#7F5EFF,#29E6BF)_border-box] dark:[background:linear-gradient(var(--bg-3),var(--bg-3))_padding-box,linear-gradient(80deg,#7F5EFF,#29E6BF)_border-box] text-foreground hover:brightness-95",
            tertiary: "border-0 bg-transparent text-foreground hover:bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)] hover:text-lavender-50 dark:text-foreground-on-accent transition-all duration-300 ease-in-out",
            tonal: "border-0 bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)] hover:bg-purple-100 text-purple-800",
            default: "bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)] text-lavender-50 dark:text-foreground-on-accent hover:brightness-[0.8] dark:hover:brightness-125",
        }
    }

    if (color && colorMap[color]) {
        return colorMap[color][variant] || defaultColor[variant]
    }

    return defaultColor[variant] || ""
}

function combinedIconColorVariant({
    color,
    variant,
    disabled,
}: {
    color?: Color;
    variant: ButtonVariant;
    disabled?: boolean;
}) {
    const defaultColor = {
        default: cn("fill-lavender-100 dark:fill-(--btn-primary-fg)", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
        // Every glyph fill tracks its variant's label so it stays visible on the dark fill.
        primary: cn("fill-lavender-100 dark:fill-(--btn-primary-fg)", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
        secondary: cn("fill-foreground", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
        tertiary: cn("fill-foreground", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
        tonal: cn(
            color ? `fill-${color}-800` : "fill-(--primary-800)",
            !color && "dark:fill-(--primary-50)",
            disabled && (color ? `fill-${color}-600` : "fill-(--primary-600)")
        ),
    };

    const colorMap = {
        purple: {
            primary: cn("fill-lavender-50 dark:fill-foreground-on-accent", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
            secondary: cn("fill-foreground", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tertiary: cn("fill-foreground", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tonal: cn("fill-purple-800 dark:fill-purple-200", disabled && "fill-purple-600"),
        },
        green: {
            primary: cn("fill-foreground dark:fill-green-800", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
            secondary: cn("fill-foreground", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tertiary: cn("fill-foreground", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tonal: cn("fill-foreground", disabled && "fill-foreground"),
        },
        "space-cadet": {
            tonal: cn("fill-space-cadet-800", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            primary: cn("fill-lavender-50 dark:fill-space-cadet-800", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
            secondary: cn("fill-foreground", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tertiary: cn("fill-foreground", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
        },
        red: {
            // Glyph tracks its label — same remap escape as the text above (styles.css 02e).
            // `primary` is left on the raw token: its icon is red-on-red today, a LIGHT bug (reported).
            primary: cn("fill-red-base", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
            secondary: cn("fill-(--btn-danger-fg)", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tertiary: cn("fill-(--btn-danger-fg)", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tonal: cn("fill-(--btn-danger-fg)", disabled && "fill-foreground"),
        },
        gradient: {
            primary: cn("fill-lavender-50 dark:fill-foreground-on-accent", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
            secondary: cn("fill-foreground", disabled && "fill-gunmetal-200 dark:fill-foreground-disabled"),
            tertiary: cn("fill-lavender-50 dark:fill-foreground-on-accent", disabled && "fill-gunmetal-100 dark:fill-foreground-disabled"),
            tonal: cn("fill-purple-800", disabled && "fill-purple-600"),
        },
    };

    if (color && colorMap[color] && colorMap[color][variant]) {
        return colorMap[color][variant];
    }

    return defaultColor[variant] || "";
}

function getLoadingClasses(variant: ButtonVariant, color: Color | undefined) {
    const defaultColorVariant = {
        primary: "bg-(--btn-primary-bg) hover:bg-(--btn-primary-hover)",
        secondary: "bg-transparent hover:bg-transparent",
        tertiary: "bg-transparent hover:bg-card",
        tonal: "bg-(--primary-100) hover:bg-(--primary-200)",
        default: "bg-(--btn-primary-bg) hover:bg-(--btn-primary-hover)"
    }

    const combinedColorVariant = {
        "space-cadet": {
            primary: "bg-space-cadet-500 hover:bg-space-cadet-600",
            secondary: "bg-transparent hover:bg-card text-foreground",
            tertiary: "bg-transparent hover:bg-card",
            tonal: "bg-space-cadet-50 hover:bg-space-cadet-100 text-foreground",
            default: "bg-space-cadet-500 hover:bg-space-cadet-600 text-lavender-50 dark:text-foreground-on-accent",
        },
        "purple": {
            primary: "bg-purple-base hover:bg-purple-600 text-lavender-50 dark:text-foreground-on-accent",
            secondary: "bg-transparent hover:bg-card text-foreground",
            tertiary: "bg-transparent hover:bg-card",
            tonal: "bg-purple-50 hover:bg-purple-100 text-purple-800",
            default: "bg-purple-base hover:bg-purple-600 text-lavender-50 dark:text-foreground-on-accent",
        },
        "green": {
            primary: "bg-green-500 hover:bg-green-600 text-lavender-50 dark:text-foreground-on-accent",
            secondary: "bg-transparent hover:bg-card text-foreground",
            tertiary: "bg-transparent hover:bg-card",
            tonal: "bg-green-50 hover:bg-green-100 text-foreground",
            default: "bg-green-500 hover:bg-green-600 text-lavender-50 dark:text-foreground-on-accent",
        },
        "red": {
            primary: "bg-red-500 hover:bg-red-600 text-lavender-50 dark:text-foreground-on-accent",
            secondary: "bg-transparent hover:bg-card text-foreground",
            tertiary: "bg-transparent hover:bg-card",
            tonal: "bg-red-50 hover:bg-red-100 text-foreground",
            default: "bg-red-500 hover:bg-red-600 text-lavender-50 dark:text-foreground-on-accent",
        },
        "gradient": {
            primary: "bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)]",
            secondary: "border-2 border-transparent [background:linear-gradient(white,white)_padding-box,linear-gradient(80deg,#7F5EFF,#29E6BF)_border-box]",
            tertiary: "bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)]",
            tonal: "bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)]",
            default: "bg-[linear-gradient(80deg,#7F5EFF,#29E6BF)]",
        }
    }

    if (color && combinedColorVariant[color]) {
        return combinedColorVariant[color][variant] || defaultColorVariant[variant]
    }

    return defaultColorVariant[variant] || ""
}

function getLoadingColor(variant: ButtonVariant, color: Color | undefined) {
    const defaultColorMap = {
        primary: "var(--btn-primary-spinner)",
        secondary: "#B2B4BA",
        tertiary: "#B2B4BA",
        tonal: "#B2B4BA",
        default: "var(--btn-primary-spinner)"
    }

    const combinedColorMap = {
        "space-cadet": {
            primary: "#fff",
            secondary: "#4B5563", // gunmetal-base
            tertiary: "#4B5563", // gunmetal-base
            tonal: "#2E3440",    // gunmetal-base for text
            default: "#fff",
        },
        "purple": {
            primary: "#fff",
            secondary: "#4B5563",
            tertiary: "#4B5563",
            tonal: "#5B21B6", // purple-800
            default: "#fff",
        },
        "green": {
            primary: "#2F3440",
            secondary: "#4B5563",
            tertiary: "#4B5563",
            tonal: "#374151", // gunmetal-base
            default: "#fff",
        },
        "red": {
            primary: "#fff",
            secondary: "#D62839", // red-base — spinner matches the red button theme
            tertiary: "#4B5563",
            tonal: "#5B21B6", // purple-800
            default: "#fff",
        },
        "gradient": {
            primary: "#fff",
            secondary: "#4B5563",
            tertiary: "#fff",
            tonal: "#5B21B6", // purple-800
            default: "#fff",
        },
    }

    if (color && combinedColorMap[color]) {
        return combinedColorMap[color][variant] || defaultColorMap[variant]
    }

    return defaultColorMap[variant] || "#B2B4BA"
}

// Define the buttonVariants function first
const buttonVariants = (color?: Color) => cva(
    // `outline-none` zeroes `--tw-outline-style`, which `outline-2` reads — so the ring needs
    // `outline-solid` too, or it emits `outline-style: none` and renders nothing.
    // `focus-visible:`, never `focus:`: a mouse click must not ring. Ring colour: styles.css 02f.
    "inline-flex items-center justify-center font-karla cursor-pointer gap-2 whitespace-nowrap rounded-lg transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--btn-focus-ring) border border-transparent aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive disabled:cursor-not-allowed disabled:bg-hover dark:disabled:border-border-strong overflow-hidden",
    {
        variants: {
            variant: {
                default: cn(
                    combinedColorVariant({ color, variant: "default" }),
                    "disabled:bg-card disabled:text-gunmetal-100 dark:disabled:text-foreground-disabled"
                ),
                primary: cn(
                    combinedColorVariant({ color, variant: "primary" }),
                    "disabled:bg-card disabled:text-gunmetal-100 dark:disabled:text-foreground-disabled"
                ),
                secondary: cn(
                    "border-gunmetal-300 dark:border-gunmetal-300-dark",
                    combinedColorVariant({ color, variant: "secondary" }),
                    "disabled:bg-card disabled:text-gunmetal-200 dark:disabled:text-foreground-disabled disabled:opacity-75"
                ),
                tertiary: cn(
                    combinedColorVariant({ color, variant: "tertiary" }),
                    "disabled:text-gunmetal-200 dark:disabled:text-foreground-disabled disabled:border-0"
                ),
                tonal: cn(
                    combinedColorVariant({ color, variant: "tonal" }),
                    "disabled:opacity-75",
                ),
                link: "text-primary-base underline-offset-4 hover:underline",
            },
            size: {
                default: "h-9 px-2 py-2 has-[>svg]:px-3",
                xs: "h-6 px-2 text-xs rounded-lg font-medium min-w-[2.685rem]",
                sm: "h-6 px-2 text-xs rounded-lg md:h-9 md:px-3 md:text-base md:rounded-[0.63rem] font-medium min-w-[4.25rem]",
                md: "h-9 px-3 md:h-12 md:px-4 text-base rounded-[0.63rem] md:rounded-xl font-medium min-w-16",
                lg: "h-12 px-4 text-base md:h-[3.5rem] md:px-6 md:text-[1.25rem] rounded-xl md:rounded-2xl font-medium min-w-24",
                // `hover:bg-neutral-3` left alone on purpose: `neutral-3` is not a defined step, so
                // the class is dead — repairing it would ADD a light hover across 4 apps.
                icon: "size-9 hover:bg-neutral-3 rounded-2xl md:rounded-full",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "sm",
        },
    }
)


const IconVariants = ({ disabled, color }: { disabled: boolean, color?: Color }) => cva(
    "flex items-center justify-center",
    {
        variants: {
            size: {
                default: "size-4",
                xs: "size-3",
                sm: "size-4",
                md: "size-4",
                lg: "size-5",
                icon: ""
            },
            variant: {
                default: cn(
                    combinedIconColorVariant({ color, variant: "default", disabled })
                ),
                primary: cn(
                    combinedIconColorVariant({ color, variant: "primary", disabled })
                ),
                secondary: cn(
                    combinedIconColorVariant({ color, variant: "secondary", disabled })
                ),
                tertiary: cn(
                    combinedIconColorVariant({ color, variant: "tertiary", disabled })
                ),
                tonal: cn(
                    combinedIconColorVariant({ color, variant: "tonal", disabled })
                ),
                link: "text-(--primary-base) underline-offset-4 hover:underline",
            },
        },
        defaultVariants: {
            size: "sm",
            variant: "primary",
        },
    }
)

type IconProps = {
    className?: string
    color?: string
} & React.SVGProps<SVGSVGElement>

interface ButtonProps extends
    Omit<React.ComponentProps<"button">, "color">,
    VariantProps<ReturnType<typeof buttonVariants>> {
    asChild?: boolean
    loading?: boolean
    color?: Color
    iconClassName?: string
    disabled?: boolean
    testId?: string
    LeftIcon?: React.ComponentType<IconProps>
    RightIcon?: React.ComponentType<IconProps>
}

function Button({
    className,
    variant,
    LeftIcon,
    RightIcon,
    color,
    size,
    iconClassName,
    loading,
    disabled = false,
    testId,
    onClick,
    ...props
}: ButtonProps) {
    const buttonVariantClasses = buttonVariants(color)
    const iconVariantClasses = IconVariants({ disabled, color })
    const isLoading = Boolean(loading)

    // A busy button is blocked WITHOUT the native `disabled` attribute: `disabled` switches on every
    // `disabled:` class (`bg-card` fill, `opacity-75`, greyed label) and washes the spinner out to the
    // point it is barely readable. Dropping the attribute while loading keeps the button at full
    // contrast; the semantics move to `aria-disabled` + `aria-busy`, `pointer-events-none` stops the
    // mouse, and the guard below cancels keyboard activation — without the `preventDefault()` a focused
    // `type="submit"` button would still submit its form on Enter mid-request.
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isLoading || disabled) {
            event.preventDefault()
            event.stopPropagation()
            return
        }
        onClick?.(event)
    }

    const loaderVariant = cva(
        "",
        {
            variants: {
                size: {
                    default: "scale-50",
                    sm: "scale-50",
                    xs: "scale-50",
                    md: "scale-75",
                    lg: "scale-100",
                    icon: ""
                }
            }
        }
    )

    return (
        <button
            disabled={disabled && !isLoading}
            aria-disabled={disabled || isLoading || undefined}
            aria-busy={isLoading || undefined}
            data-slot="button"
            data-testid={testId}
            className={cn(
                buttonVariantClasses({ variant, size, className }),
                isLoading ? cn(getLoadingClasses(variant, color), 'dark:border-border-strong pointer-events-none') : ''
            )}
            {...props}
            onClick={handleClick}
        >
            {loading ? (
                <div className={cn("origin-center mt-1", loaderVariant({ size }))}>
                    <Spinner color={getLoadingColor(variant, color)} />
                </div>
            ) : (
                <div className="flex items-center justify-center gap-1">
                    {LeftIcon && <LeftIcon className={iconVariantClasses({ size, variant, className: iconClassName })} />}
                    {props.children}
                    {RightIcon && <RightIcon className={iconVariantClasses({ size, variant, className: iconClassName })} />}
                </div>
            )}
        </button>
    )
}

export default Button
export { buttonVariants, IconVariants, type ButtonProps, type Color, type ButtonVariant }